import React, { Activity, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { SortableContext } from '@dnd-kit/sortable';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import styled from 'styled-components';
import Switch from '@mui/material/Switch';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { FolderManagerActionTypes, FolderManagerItemActionNames, FolderManagerMimeTypes } from '../../../../interfaces';
import { viewStateSelectors } from '../../../../store';
import { markAlreadyAddedSlides } from '../../../../helpers';
import { DataStatusHandler } from '../DataStatusHandler';
import { DraggableDnDContainer } from '../DraggableDnDContainer';
import { FileDropContainer } from '../FileDropContainer';
import { FolderManagerGridItem } from '../FolderManagerGridItem';
import { StyledGridContainer } from '../folderManagerGrid.shared';
import {
  DEFAULT_SLIDES_FILTERS,
  filterAndSortSlides,
  GROUP_BY_OPTIONS,
  SlidesViewFilters,
  filterAndSortDecks,
} from '../FolderManagerSlidesViewFilters';
import { FolderRow, FolderRowProps, FolderActionsConfig, isFolder, isDeck, isAsset } from './components/FolderRow';
import { SlidePreviewDialog } from '../../../../pages/Presentations/components/SlidePreviewDialog';
import { SlidesControlBar } from '../SlidesControlBar';
import { NgDivider } from '../../../UI';
import { useUploadNewAsset } from '../../UploadNewAssetContext';

//region INTERFACES

export const ROOT_DROP_ID = '__ROOT__';

// Group-by-PPTX toggle is on by default and temporarily hidden from users; flip to re-expose the UI control.
const SHOW_GROUP_BY_PPTX_SWITCH = false;

export interface FolderManagerFoldersViewProps {
  items: FolderManagerItem[];
  selectedRows: FolderManagerItem[];
  isLoading: boolean;
  noDataMessage?: string;
  minHeight?: string;
  viewStateKey?: string;
  acceptedDragTypes?: string[];
  disableUpload?: boolean;
  showAssets?: boolean;
  isSlideRepositoryFolder?: boolean;
  enableDnD?: boolean;
  disableSelectAll?: boolean;
  rootFolderId?: number;
  selectedTheme?: number;
  addActionName?: FolderManagerItemActionNames;
  actionsConfig?: FolderActionsConfig;
  selectedKeys?: Set<string>;
  filters?: SlidesViewFilters;
  enableGroupByPptx?: boolean;
  showControls?: boolean;
  slideRepositoryId?: number;
  onFiltersChange?: (filters: SlidesViewFilters) => void;
  onAction: (data: any) => void;
  onSelect: (data: any) => void;
  onDoubleClick: (data: any) => void;
  onDragStart: (data: any) => void;
  onDragEnd: (data: any) => void;
}

interface FolderRowsContainerProps {
  items: FolderManagerItem[];
  overId?: string | number | null;
  sharedRowProps: Omit<FolderRowProps, 'item' | 'depth' | 'overId'>;
}

const FolderRowsContainer = React.memo(({ items, overId, sharedRowProps }: FolderRowsContainerProps) => (
  <>
    {items.map((item) => (
      <FolderRow key={`folder-${item.id}`} item={item} depth={0} overId={overId} {...sharedRowProps} />
    ))}
  </>
));
//endregion

const StyledGroupByToggleBar = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 4px 0 8px;
`;

const StyledUploadHint = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  margin-top: -${({ theme }) => theme.spacing.small};
  padding: 0px ${({ theme }) => theme.spacing.small};
  color: ${({ theme }) => theme.palette.textDimmed};
  font-size: ${({ theme }) => theme.fontSize.xs};

  svg {
    flex-shrink: 0;
    font-size: 18px;
    color: ${({ theme }) => theme.palette.textDimmed};
  }
`;

const StyledGroupBySwitch = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 500;
  color: ${({ theme }) => theme.palette.textDimmed};
  cursor: pointer;
  white-space: nowrap;
  user-select: none;
`;

const MIN_ITEM_WIDTH = 120;
const MAX_COLS = 5;
const ITEM_GAP = 8;
const ESTIMATED_ROW_HEIGHT = 140;

const StyledVirtualInner = styled.div<{ $totalHeight: number }>`
  height: ${({ $totalHeight }) => $totalHeight}px;
  position: relative;
`;

const StyledVirtualRow = styled.div<{ $top: number; $numCols: number }>`
  position: absolute;
  top: ${({ $top }) => $top}px;
  left: 0;
  right: 0;
  display: grid;
  grid-template-columns: repeat(${({ $numCols }) => $numCols}, 1fr);
  grid-gap: ${({ theme }) => theme.spacing.small};
  grid-row-gap: 3px;
  align-items: start;
  padding: 0 0 8px;
`;

interface VirtualFlatSlideGridProps {
  slides: FolderManagerItem[];
  selectedRows: FolderManagerItem[];
  selectedKeys?: Set<string>;
  acceptedDragTypes?: string[];
  viewStateKey?: string;
  actionsConfig?: FolderActionsConfig;
  disableSelectAll: boolean;
  disableUpload?: boolean;
  addActionName: FolderManagerItemActionNames;
  visible: boolean;
  onAction: (data: any) => void;
  onSelect: (data: any) => void;
  onDoubleClick: (data: any) => void;
}

const VirtualFlatSlideGrid: React.FC<VirtualFlatSlideGridProps> = ({
  slides,
  selectedRows,
  selectedKeys,
  acceptedDragTypes,
  viewStateKey,
  actionsConfig,
  disableSelectAll,
  disableUpload,
  addActionName,
  visible,
  onAction,
  onSelect,
  onDoubleClick,
}) => {
  const slideActionsConfig = actionsConfig?.[FolderManagerMimeTypes.SLIDE];
  const hideSlideActions = Array.isArray(slideActionsConfig) && slideActionsConfig.length === 0;
  const listRef = useRef<HTMLDivElement>(null);
  const [numCols, setNumCols] = useState(MAX_COLS);
  const [previewState, setPreviewState] = useState<{ currentIndex: number } | null>(null);
  const selectedThemeId = useSelector(viewStateSelectors.getSelectedThemeId(viewStateKey || ''));

  const selectableSlides = useMemo(() => markAlreadyAddedSlides(slides, selectedKeys), [slides, selectedKeys]);

  const rows = useMemo(() => {
    const result: FolderManagerItem[][] = [];
    for (let i = 0; i < selectableSlides.length; i += numCols) {
      result.push(selectableSlides.slice(i, i + numCols) as FolderManagerItem[]);
    }
    return result;
  }, [selectableSlides, numCols]);

  const sortableIds = useMemo(() => slides.map((slide: any) => String(slide.instanceId ?? slide.id)), [slides]);

  const virtualizer = useWindowVirtualizer({
    count: rows.length,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: 4,
    scrollMargin: listRef.current?.offsetTop ?? 0,
  });

  const handleItemSelect = useCallback(
    (data: any) => onSelect({ ...data, visibleItems: selectableSlides }),
    [onSelect, selectableSlides],
  );

  const handleItemAction = useCallback(
    (data: any) => {
      if (data?.actionType === FolderManagerActionTypes.SLIDE_PREVIEW) {
        const clickedItem = data.payload.item;
        const idx = slides.findIndex(
          (s: any) => (clickedItem.instanceId && s.instanceId === clickedItem.instanceId) || s.id === clickedItem.id,
        );
        setPreviewState({ currentIndex: idx >= 0 ? idx : 0 });
        return;
      }
      onAction(data);
    },
    [onAction, slides],
  );

  useLayoutEffect(() => {
    if (visible) {
      virtualizer.measure();
    }
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = listRef.current;

    if (!el) {
      return;
    }

    let rafId: number;

    const row = new ResizeObserver(([entry]) => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const newCols = Math.min(
          MAX_COLS,
          Math.max(1, Math.floor((entry.contentRect.width + ITEM_GAP) / (MIN_ITEM_WIDTH + ITEM_GAP))),
        );
        setNumCols((prev) => (prev !== newCols ? newCols : prev));
      });
    });

    row.observe(el);

    return () => {
      row.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <>
      <div ref={listRef}>
        <StyledVirtualInner $totalHeight={virtualizer.getTotalSize()}>
          <SortableContext items={sortableIds}>
            {virtualizer.getVirtualItems().map((virtualRow) => (
              <StyledVirtualRow
                key={virtualRow.index}
                ref={virtualizer.measureElement}
                data-index={virtualRow.index}
                $top={virtualRow.start - virtualizer.options.scrollMargin}
                $numCols={numCols}
              >
                {rows[virtualRow.index].map((slide: any) => {
                  const slideItem =
                    slideActionsConfig !== undefined ? { ...slide, actions: slideActionsConfig } : slide;
                  const key = `${slide.type}-${slide.instanceId ?? slide.id}`;
                  const isSingleSlideAsset = (slide.parent?.slides?.length ?? 0) === 1;
                  const isAlreadyAdded = slide.selectable === false;

                  return (
                    <FileDropContainer
                      key={key}
                      isItemContainer
                      isDisabled={!isSingleSlideAsset || !!disableUpload}
                      onUpload={(fileList) =>
                        onAction({
                          actionType: FolderManagerActionTypes.UPLOAD,
                          payload: { files: fileList, item: slide.parent },
                        })
                      }
                    >
                      <FolderManagerGridItem
                        item={slideItem}
                        selectedRows={selectedRows}
                        acceptedDragTypes={acceptedDragTypes}
                        viewStateKey={viewStateKey}
                        compact
                        isUploadDisabled
                        hideActions={hideSlideActions}
                        alreadyAdded={isAlreadyAdded}
                        disableDnD={isAlreadyAdded}
                        onAction={handleItemAction}
                        onSelect={handleItemSelect}
                        onDoubleClick={() => onDoubleClick({ row: slide })}
                        onClick={
                          disableSelectAll
                            ? undefined
                            : () => onAction({ actionType: addActionName, payload: { selectedItems: [slide] } })
                        }
                      />
                    </FileDropContainer>
                  );
                })}
              </StyledVirtualRow>
            ))}
          </SortableContext>
        </StyledVirtualInner>
      </div>

      {previewState !== null && (
        <SlidePreviewDialog
          folderSlides={slides}
          deckName={(slides[previewState.currentIndex] as any)?.parent?.displayName ?? ''}
          currentIndex={previewState.currentIndex}
          selectedKeys={selectedKeys}
          selectedThemeId={selectedThemeId}
          hideAddButton={disableSelectAll}
          onClose={() => setPreviewState(null)}
          onNavigate={(index) => setPreviewState({ currentIndex: index })}
          onToggle={(_, checked) => {
            const state = previewState;
            if (!state) return;
            const currentItem = slides[state.currentIndex];
            if (!currentItem) return;
            const actionType = checked
              ? FolderManagerItemActionNames.ADD_TO_PRESENTATION
              : FolderManagerItemActionNames.REMOVE_FROM_PRESENTATION;
            onAction({ actionType, payload: { selectedItems: [currentItem] } });
          }}
        />
      )}
    </>
  );
};

export const FolderManagerFoldersView = ({
  items,
  selectedRows,
  isLoading,
  noDataMessage,
  minHeight,
  viewStateKey,
  acceptedDragTypes,
  disableUpload,
  showAssets = false,
  isSlideRepositoryFolder,
  enableDnD = false,
  disableSelectAll = false,
  rootFolderId,
  addActionName = FolderManagerItemActionNames.ADD_TO_PRESENTATION,
  actionsConfig,
  selectedKeys,
  filters,
  enableGroupByPptx = false,
  showControls = false,
  slideRepositoryId,
  onFiltersChange,
  selectedTheme,
  onAction,
  onSelect,
  onDoubleClick,
  onDragStart,
  onDragEnd,
}: FolderManagerFoldersViewProps) => {
  const [groupByPptx, setGroupByPptx] = useState(true);
  const { openUploadNewAsset } = useUploadNewAsset();
  const [collapsedDeckIds, setCollapsedDeckIds] = useState<Set<string | number>>(new Set());

  const toggleDeckExpanded = useCallback(
    (id: string | number) =>
      setCollapsedDeckIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    [],
  );
  const deferredFilters = filters || DEFAULT_SLIDES_FILTERS;
  const effectiveGroupByPptx = enableGroupByPptx ? groupByPptx : deferredFilters.groupBy === GROUP_BY_OPTIONS.PPTX;
  const effectiveFilters = useMemo(
    () => ({ ...deferredFilters, groupBy: effectiveGroupByPptx ? GROUP_BY_OPTIONS.PPTX : GROUP_BY_OPTIONS.NONE }),
    [deferredFilters, effectiveGroupByPptx],
  );

  // when grouping is forced (enableGroupByPptx), PPTX-grouped is the default for this view, not GROUP_BY_OPTIONS.NONE —
  // otherwise the filters-active indicator lights up on an untouched view.
  const viewDefaultFilters = useMemo(
    () => (enableGroupByPptx ? { ...DEFAULT_SLIDES_FILTERS, groupBy: GROUP_BY_OPTIONS.PPTX } : DEFAULT_SLIDES_FILTERS),
    [enableGroupByPptx],
  );

  const handleFiltersChange = useCallback(
    (nextFilters: SlidesViewFilters) => {
      if (!enableGroupByPptx) {
        onFiltersChange?.(nextFilters);
        return;
      }

      const displayedGroupBy = effectiveGroupByPptx ? GROUP_BY_OPTIONS.PPTX : GROUP_BY_OPTIONS.NONE;
      const groupByChanged = nextFilters.groupBy !== displayedGroupBy;

      if (groupByChanged) {
        setGroupByPptx(nextFilters.groupBy === GROUP_BY_OPTIONS.PPTX);
        onFiltersChange?.(nextFilters);
      } else {
        // groupBy here is just the forced display value carried over from effectiveFilters, not a real
        // user choice — restore the underlying value so it doesn't get persisted as a side effect.
        onFiltersChange?.({ ...nextFilters, groupBy: deferredFilters.groupBy });
      }
    },
    [enableGroupByPptx, effectiveGroupByPptx, deferredFilters.groupBy, onFiltersChange],
  );

  const folderItems = useMemo(() => items.filter(isFolder), [items]);

  // all decks, unfiltered by date - flatSlides applies the date filter per-slide instead, since
  // a deck's own updatedAt shouldn't gate the visibility of its individual slides
  const decks = useMemo(
    () => items.filter((item) => isDeck(item, isSlideRepositoryFolder)),
    [items, isSlideRepositoryFolder],
  );

  const pptxItems = useMemo(() => filterAndSortDecks(decks, deferredFilters), [decks, deferredFilters]);

  const assetsItems = useMemo(
    () => (showAssets ? items.filter((item) => isAsset(item, isSlideRepositoryFolder)) : []),
    [showAssets, items, isSlideRepositoryFolder],
  );

  const flatSlides = useMemo(() => {
    const itemsWithDates = decks.flatMap((deck) =>
      (deck.slides || []).map((slide: any) => ({
        ...slide,
        parent: deck,
        updatedAt: slide.itemData?.UpdatedAt ?? deck.updatedAt,
        createdAt: slide.itemData?.CreatedAt ?? deck.createdAt,
      })),
    );

    return filterAndSortSlides(itemsWithDates, deferredFilters);
  }, [decks, deferredFilters]);

  const sharedRowProps = useMemo(
    () => ({
      selectedRows,
      acceptedDragTypes,
      viewStateKey,
      selectedKeys,
      enableDnD,
      disableUpload,
      isParentSlideRepositoryFolder: isSlideRepositoryFolder,
      disableSelectAll,
      filters: effectiveFilters,
      addActionName,
      actionsConfig,
      onAction,
      onSelect,
      onDoubleClick,
    }),
    [
      selectedRows,
      acceptedDragTypes,
      viewStateKey,
      selectedKeys,
      enableDnD,
      disableUpload,
      isSlideRepositoryFolder,
      disableSelectAll,
      effectiveFilters,
      addActionName,
      actionsConfig,
      selectedTheme,
      onAction,
      onSelect,
      onDoubleClick,
    ],
  );

  const groupedPptxRows = useMemo(
    () =>
      pptxItems.map((item) => (
        <FolderRow
          key={`pptx-${item.id}`}
          item={item}
          depth={0}
          parentFolderId={rootFolderId ?? 0}
          {...sharedRowProps}
          isExpanded={!collapsedDeckIds.has(item.id)}
          onToggleExpand={() => toggleDeckExpanded(item.id)}
        />
      )),
    [pptxItems, sharedRowProps, collapsedDeckIds, toggleDeckExpanded, rootFolderId],
  );

  const dndItems = useMemo(
    () => (effectiveGroupByPptx ? [...folderItems, ...pptxItems] : folderItems),
    [effectiveGroupByPptx, folderItems, pptxItems],
  );

  const pptxContent = (
    <>
      <Activity mode={effectiveGroupByPptx ? 'visible' : 'hidden'}>{groupedPptxRows}</Activity>
      {flatSlides.length > 0 && (
        <Activity mode={effectiveGroupByPptx ? 'hidden' : 'visible'}>
          <VirtualFlatSlideGrid
            slides={flatSlides as FolderManagerItem[]}
            selectedRows={selectedRows}
            selectedKeys={selectedKeys}
            acceptedDragTypes={acceptedDragTypes}
            viewStateKey={viewStateKey}
            actionsConfig={actionsConfig}
            disableSelectAll={disableSelectAll}
            disableUpload={disableUpload}
            addActionName={addActionName}
            visible={!effectiveGroupByPptx}
            onAction={onAction}
            onSelect={onSelect}
            onDoubleClick={onDoubleClick}
          />
        </Activity>
      )}
    </>
  );

  const handleRootUpload = (fileList: FileList) => {
    if (isSlideRepositoryFolder) {
      openUploadNewAsset(fileList[0], rootFolderId ?? 0);
      return;
    }

    onAction({ actionType: FolderManagerActionTypes.UPLOAD, payload: { files: fileList, item: null } });
  };

  return (
    <>
      <FileDropContainer isDisabled={disableUpload} minHeight={minHeight} onUpload={handleRootUpload}>
        <StyledGridContainer style={{ minHeight: minHeight || '60vh' }}>
          {!disableUpload && isSlideRepositoryFolder && (
            <>
              <SlidesControlBar
                leftItems={
                  <StyledUploadHint>
                    <CloudUploadOutlinedIcon />
                    Drag any .pptx file into a folder or subfolder to upload a new deck. <br /> Drag a .pptx file onto
                    an existing file to create a new version of that deck.
                  </StyledUploadHint>
                }
                showControls={showControls}
                filters={effectiveFilters}
                defaultFilters={viewDefaultFilters}
                onFiltersChange={handleFiltersChange}
                viewStateKey={viewStateKey}
                slideRepositoryId={slideRepositoryId}
              />

              <NgDivider spacing="small" />
            </>
          )}

          {enableGroupByPptx && SHOW_GROUP_BY_PPTX_SWITCH && (
            <StyledGroupByToggleBar>
              <StyledGroupBySwitch>
                <Switch size="small" checked={groupByPptx} onChange={(e) => setGroupByPptx(e.target.checked)} />
                Group by PPTX
              </StyledGroupBySwitch>
            </StyledGroupByToggleBar>
          )}

          <DataStatusHandler
            isLoading={isLoading}
            showLoader={isLoading}
            showNoDataMessage={!!noDataMessage}
            message={noDataMessage}
          />

          {enableDnD ? (
            <DraggableDnDContainer
              items={dndItems}
              onDragEnd={onDragEnd}
              onDragStart={onDragStart}
              enableRootDrop={!!rootFolderId}
              dragOverlayItems={selectedRows}
            >
              <FolderRowsContainer items={folderItems} sharedRowProps={sharedRowProps} />
              {pptxContent}
            </DraggableDnDContainer>
          ) : (
            <>
              <FolderRowsContainer items={folderItems} sharedRowProps={sharedRowProps} />
              {pptxContent}
            </>
          )}

          <Activity mode={assetsItems.length > 0 ? 'visible' : 'hidden'}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                gap: '8px',
                padding: '8px 0 12px',
              }}
            >
              {assetsItems.map((item) => (
                <FolderManagerGridItem
                  key={`${item.type}-${item.id}`}
                  item={item}
                  selectedRows={selectedRows}
                  acceptedDragTypes={acceptedDragTypes}
                  viewStateKey={viewStateKey}
                  isUploadDisabled={disableUpload}
                  onAction={onAction}
                  onSelect={onSelect}
                  onDoubleClick={() => onDoubleClick({ row: item })}
                  onClick={() =>
                    onAction({
                      actionType: FolderManagerItemActionNames.ADD_TO_PRESENTATION,
                      payload: { selectedItems: [item] },
                    })
                  }
                />
              ))}
            </div>
          </Activity>
        </StyledGridContainer>
      </FileDropContainer>
    </>
  );
};
